import React, { useState, useRef, useCallback, useEffect } from "react";
import { Grid, Typography, Tooltip } from "@material-ui/core";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { map } from "../../../../config.json";
import { mapStyles } from "../../../../Util/CSS/MapStyles";
import { MyLocation } from "@material-ui/icons";
import { PrimaryButton } from "../../../../Shared/Buttons";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import axios from "axios";

import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";

import "@reach/combobox/styles.css";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "300px",
};
const options = {
  styles: mapStyles,
  disableDefaultUI: false,
  zoomControl: true,
};

const center = {
  lat: 42.360081,
  lng: -71.058884,
};

const getLocationDetails = async (lat, lng, apiKey) => {
  let latlng = `latlng=${lat},${lng}`;
  const api = `https://maps.googleapis.com/maps/api/geocode/json?${latlng}&key=${apiKey}`;
  try {
    let res = await fetch(api);
    const data = await res.json();
    return { result: data.results[0] };
  } catch (err) {
    console.log("map error", err);
  }
};

const getValue = (address_components, field) => {
  let x;
  address_components.forEach((component) => {
    if (component.types.includes(field)) {
      x = component.long_name;
      return false;
    }
  });
  if (x) {
    return x;
  }
};

const Map = ({ classes, showHeader, settings, setSettings }) => {
  const { apiKey } = map;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [marker, setMarker] = useState(null);

  const mapRef = useRef();
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback(({ lat, lng }) => {
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(17);
  }, []);

  useEffect(() => {
    if (
      settings?.generalSettings?.location?.latlng?.lat &&
      settings?.generalSettings?.location?.latlng?.lng
    ) {
      setMarker({
        lat: settings.generalSettings.location.latlng.lat,
        lng: settings.generalSettings.location.latlng.lng,
      });
    }
  }, [
    settings?.generalSettings?.location?.latlng?.lat,
    settings?.generalSettings?.location?.latlng?.lng,
  ]);

  // const setLocationOld = useCallback(
  //   (data) => {
  //     let location = {
  //       formatted_address: data.result.formatted_address,
  //       landmark:
  //         getValue(data.result.address_components, "sublocality") ||
  //         getValue(data.result.address_components, "neighborhood") ||
  //         getValue(data.result.address_components, "locality") ||
  //         "",
  //       street: getValue(data.result.address_components, "street_number")
  //         ? getValue(data.result.address_components, "street_number") +
  //           " " +
  //           getValue(data.result.address_components, "route")
  //         : getValue(data.result.address_components, "route")
  //         ? getValue(data.result.address_components, "route")
  //         : getValue(data.result.address_components, "sublocality_level_2") ||
  //           "  ",
  //       postal_code: getValue(
  //         data.result.address_components,
  //         "postal_code_suffix"
  //       )
  //         ? getValue(data.result.address_components, "postal_code") +
  //           " - " +
  //           getValue(data.result.address_components, "postal_code_suffix")
  //         : getValue(data.result.address_components, "postal_code"),
  //       city: getValue(
  //         data.result.address_components,
  //         "administrative_area_level_2"
  //       ),
  //       state: getValue(
  //         data.result.address_components,
  //         "administrative_area_level_1"
  //       ),
  //       country: getValue(data.result.address_components, "country"),
  //       latlng: {
  //         lat: data.result.geometry.location.lat,
  //         lng: data.result.geometry.location.lng,
  //       },
  //     };
  //     let obj = settings.generalSettings;
  //     obj.location = location;
  //     setSettings({ ...settings, generalSettings: obj });
  //     //   handleSnackbarOpen("Address Updated✔","successDark")
  //   },
  //   [settings, setSettings]
  // );

  const setLocation = useCallback(
    (data) => {
      console.log("---location Detail---", data);
      let location = {
        formatted_address: data.result.formatted_address,
        landmark: "",
        street: data.result.formatted_address.split(",")[0],
        postal_code:
          getValue(data.result.address_components, "postal_code") || "",
        city: getValue(data.result.address_components, "locality") || "",
        state:
          getValue(
            data.result.address_components,
            "administrative_area_level_1"
          ) || "",
        country: getValue(data.result.address_components, "country"),
        latlng: {
          lat: data.result.geometry.location.lat,
          lng: data.result.geometry.location.lng,
        },
        place_id: data.result?.place_id || "",
        map_url: data.result?.url || "",
      };
      let obj = settings.generalSettings;
      obj.location = location;
      setSettings({ ...settings, generalSettings: obj });
    },
    [settings, setSettings]
  );

  const onMapClick = useCallback(
    async (event) => {
      setMarker({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
      const data = await getLocationDetails(
        event.latLng.lat(),
        event.latLng.lng(),
        apiKey
      );
      setLocation(data);
    },
    [apiKey, setLocation]
  );

  if (loadError) return "Error loading maps";
  if (!isLoaded) return "Loading Maps";

  return (
    <Grid container direction="row" spacing={1}>
      {showHeader && (
        <>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1" className={classes.typographyStyles}>
              Pin your location
            </Typography>
          </Grid>
          <Grid item xs={9} sm={11} style={{ marginTop: "10px" }}>
            <Search
              classes={classes}
              panTo={panTo}
              apiKey={apiKey}
              setLocation={setLocation}
            />
          </Grid>
          <Tooltip
            title="Recenter"
            placement="bottom"
            style={{ marginTop: "10px" }}
          >
            <Grid item xs={3} sm={1}>
              <PrimaryButton
                type="success"
                rounded={true}
                handleClick={() =>
                  navigator.geolocation.getCurrentPosition(
                    async (position) => {
                      panTo({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      });
                      const data = await getLocationDetails(
                        position.coords.latitude,
                        position.coords.longitude,
                        apiKey
                      );
                      setLocation(data);
                    },
                    () => null
                  )
                }
              >
                <MyLocation />
              </PrimaryButton>
            </Grid>
          </Tooltip>
        </>
      )}
      <Grid item xs={12} className={classes.mt10}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={8}
          center={center}
          options={options}
          onClick={onMapClick}
          onLoad={onMapLoad}
        >
          {marker && (
            <Marker
              position={{
                lat: marker.lat,
                lng: marker.lng,
              }}
            />
          )}
        </GoogleMap>
      </Grid>
    </Grid>
  );
};

export default Map;

const Search = ({ classes, panTo, apiKey, setLocation }) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: {
        lat: () => center.lat,
        lng: () => center.lng,
      },
      radius: 200 * 1000,
    },
  });

  const getLocationDetailsByPlaceId = async (placeId) => {
    try {
      let apiEndPoint = `${process.env.REACT_APP_COMMON_NODE_API_URL}location/${placeId}`;
      const { data } = await axios.get(apiEndPoint);
      if (data && data.result) {
        return data.result;
      } else {
        return null;
      }
    } catch (err) {
      console.log("map error", err);
    }
  };
  const onSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();
    try {
      const result = await getGeocode({ address });
      const locationDetail = await getLocationDetailsByPlaceId(
        result[0].place_id
      );
      if (locationDetail) {
        let lat = locationDetail.geometry.location.lat;
        let lng = locationDetail.geometry.location.lng;
        panTo({ lat, lng });
        setLocation({ result: locationDetail });
      }
    } catch (error) {}
  };

  return (
    <>
      <Combobox onSelect={(address) => onSelect(address)}>
        <ComboboxInput
          className={classes.inputStyle}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!ready}
          placeholder="Enter an address"
        />
        <ComboboxPopover>
          <ComboboxList>
            {status === "OK" &&
              data.map(({ description }) => (
                <ComboboxOption
                  key={description}
                  value={description}
                  style={{ color: "#000" }}
                />
              ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
    </>
  );
};
